import CheckoutVehicles from '@allesauto/core/checkoutVehicles';
import {
  PaymentType,
  ProductType,
  SchemaTypes,
  ShippingType,
} from '@allesauto/graphql';
import translate from '@allesauto/i18n';
import { useCallback } from 'react';
import React, { FC } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import FinanceDetailsOverlay from '@/components/forms/FinanceDetailsOverlay/FinanceDetailsOverlay';

import { featureflags } from '@/global/featureflags/featureflags';
import { getFinanceDetails } from '@/global/funcs/financeDetails';
import useToken from '@/global/providers/auth';
import { closeOverlay, openOverlay } from '@/store/redux/actions/app';

export interface CheckoutSideBarProps {
  cartItems: SchemaTypes.CartItem[];
  trackingId?: string;
}

const CheckoutSideBar: FC<React.PropsWithChildren<CheckoutSideBarProps>> = ({
  cartItems,
  trackingId,
}) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const isLoggedIn = !!useToken();
  const openOverlayFinance = useCallback(
    (
      paymentType: PaymentType.LeasingRateRange | PaymentType.LoanRateRange,
      legalText: string | undefined,
      calculation: SchemaTypes.FinanceCalculationResult | undefined | null,
      vehicleName: string
    ) =>
      dispatch(
        openOverlay({
          title: translate(intl, `ui.PDP.FOOTER.FINANCE_TYPE.${paymentType}`),
          children: (
            <FinanceDetailsOverlay
              legalText={legalText || ''}
              summaryText={getFinanceDetails(intl, calculation ?? undefined)}
              closeOverlay={() => dispatch(closeOverlay())}
              vehicleName={vehicleName}
              financeTypeText={translate(
                intl,
                `ui.FINANCE_DETAILS.${paymentType}`
              )}
            />
          ),
        })
      ),
    [dispatch, intl]
  );

  const hasAccessoriesShop = featureflags.accessoriesShopEnabled;

  const standAloneAccessories = cartItems
    .filter(
      (cartItem) =>
        (cartItem.productType &&
          cartItem.productType === ProductType.AccessoryStandAlone) ||
        cartItem.productType === ProductType.Shipment
    )
    .map((cartItem) => {
      const accessory = cartItem.product as SchemaTypes.Accessory;

      return {
        name: accessory.title,
        numOfProducts: cartItem.quantity,
        price: cartItem.payment.price ?? 0,
        dealer: accessory.dealerId,
        delivery: '',
        sku: cartItem.sku,
        relatedProductSku: cartItem.relatedProductSku,
      };
    });

  const sideBarVehicles = cartItems
    .filter(
      (cartItem) =>
        cartItem.productType && cartItem.productType === ProductType.Vehicle
    )
    .map((cartItem) => {
      const product = cartItem.product as SchemaTypes.Vehicle;
      const extras = cartItems
        .filter(
          (extraItem) =>
            extraItem.relatedProductSku === cartItem.sku &&
            [
              ProductType.Service,
              ProductType.Accessory,
              ProductType.Shipment,
            ].includes(extraItem.productType ?? ProductType.Vehicle)
        )
        .map((extraItem) => {
          if (extraItem.productType === ProductType.Shipment) {
            const shipment = extraItem.product as SchemaTypes.Shipment;

            return {
              label: translate(
                intl,
                `ui.CHECKOUT.SIDE_BAR.SHIPMENT_${
                  shipment?.shippingType ?? ShippingType.Delivery
                }`
              ),
              value: shipment?.price ?? 0,
            };
          } else {
            return {
              label: extraItem.product?.title ?? '',
              value: extraItem.payment?.price ?? 0,
            };
          }
        });

      const vehicleName = `${product.makeName ?? ''} ${
        product.modelName ?? ''
      }`;
      const paymentType = cartItem.payment?.paymentType;

      return {
        modelName: vehicleName,
        monthlyPrice:
          cartItem.payment?.calculation?.monthlyInstallment &&
          cartItem.payment?.calculation?.monthlyInstallment > 0
            ? cartItem.payment?.calculation?.monthlyInstallment
            : undefined,
        downPayment:
          cartItem.payment.calculation?.downPayment !== undefined
            ? cartItem.payment.calculation?.downPayment ?? 0
            : undefined,
        totalPrice: cartItem.payment?.price ?? 0,
        additionalProducts: extras,
        openFinanceOverlay:
          paymentType && paymentType !== PaymentType.CashRange
            ? () =>
                openOverlayFinance(
                  paymentType,
                  cartItem.payment?.bankSummary ?? '',
                  cartItem.payment?.calculation,
                  vehicleName
                )
            : undefined,
        paymentType: paymentType,
      };
    });

  const tradeInCars = cartItems
    .filter(
      (cartItem) =>
        cartItem.productType &&
        cartItem.productType === ProductType.TradeInVehicle
    )
    .map((cartItem) => {
      const product = cartItem.product as SchemaTypes.TradeInVehicle;

      return {
        name: `${product.make} ${product.model}`,
        price: product.vehicleFinalPrice ?? 0,
      };
    });

  return (
    <CheckoutVehicles
      vehicles={sideBarVehicles}
      standAloneAccessories={standAloneAccessories}
      withTradeIn={isLoggedIn && tradeInCars.length > 0}
      tradeInCars={tradeInCars}
      trackingId={trackingId}
      hasAccessoriesShop={hasAccessoriesShop}
    />
  );
};

export default CheckoutSideBar;
